import type { ReactElement } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import api from '../lib/api'

const API_BASE = (api.defaults.baseURL as string) || '/tools/api/public/api/v1'

type Provider = 'google' | 'microsoft' | 'github' | 'facebook'

const META: Record<Provider, { label: string; bg: string; icon: ReactElement }> = {
  google: {
    label: 'Google',
    bg: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
      </svg>
    ),
  },
  microsoft: {
    label: 'Microsoft',
    bg: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 23 23" aria-hidden="true">
        <path fill="#F25022" d="M1 1h10v10H1z"/><path fill="#7FBA00" d="M12 1h10v10H12z"/>
        <path fill="#00A4EF" d="M1 12h10v10H1z"/><path fill="#FFB900" d="M12 12h10v10H12z"/>
      </svg>
    ),
  },
  github: {
    label: 'GitHub',
    bg: 'bg-[#24292f] text-white border-[#24292f] hover:bg-[#1b1f24]',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 .5C5.73.5.5 5.74.5 12.02c0 5.1 3.29 9.42 7.86 10.96.58.1.79-.25.79-.56v-2c-3.2.7-3.87-1.37-3.87-1.37-.53-1.34-1.3-1.7-1.3-1.7-1.06-.72.08-.71.08-.71 1.17.08 1.79 1.2 1.79 1.2 1.04 1.79 2.73 1.27 3.4.97.1-.76.41-1.27.74-1.56-2.55-.29-5.23-1.28-5.23-5.7 0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.8 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.84 1.19 3.1 0 4.43-2.69 5.41-5.25 5.69.42.36.8 1.08.8 2.18v3.23c0 .31.21.67.8.56A11.53 11.53 0 0 0 23.5 12C23.5 5.74 18.27.5 12 .5z"/>
      </svg>
    ),
  },
  facebook: {
    label: 'Facebook',
    bg: 'bg-[#1877F2] text-white border-[#1877F2] hover:bg-[#0e64d6]',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"/>
      </svg>
    ),
  },
}

/** Tlačítka pro sociální přihlášení/registraci. Zobrazí jen providery nakonfigurované na backendu. */
export default function SocialButtons({ next }: { next?: string }) {
  const { t } = useTranslation()
  const { data } = useQuery({
    queryKey: ['social-providers'],
    queryFn: () => api.get('/auth/providers').then((r) => r.data),
    staleTime: 5 * 60_000,
  })
  const providers: Provider[] = data?.providers ?? []
  if (providers.length === 0) return null

  const nextParam = next ?? (window.location.pathname + window.location.search)
  const href = (p: Provider) => `${API_BASE}/auth/${p}/url?next=${encodeURIComponent(nextParam)}`

  return (
    <div className="space-y-2.5">
      <div className="relative my-4 text-center">
        <span className="relative z-10 px-3 text-xs text-gray-400 bg-white dark:bg-transparent">{t('auth.or_continue_with')}</span>
        <div className="absolute inset-x-0 top-1/2 h-px bg-gray-200 dark:bg-white/10" />
      </div>
      <div className="grid grid-cols-1 gap-2.5">
        {providers.map((p) => (
          <a key={p} href={href(p)}
            className={`flex items-center justify-center gap-2.5 w-full px-4 py-2.5 rounded-xl border text-sm font-semibold transition ${META[p].bg}`}>
            {META[p].icon}
            {t('auth.continue_with', { provider: META[p].label })}
          </a>
        ))}
      </div>
    </div>
  )
}
